import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatCharge, formatDateTime } from "../utils/formatCharge";
import "../../../../../css/Finance/Charges/ChargesVersionHistory.css";

const catalogOrder = {
  financial_planning: 1,
  mortgage_protection: 2,
};

const ChargesVersionHistory = ({ versionBatches = [], currentVersionId }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  const currentVersionRef = useRef(null);

  const sortedBatches = useMemo(() => {
    return [...(versionBatches || [])].sort((a, b) => {
      const aIsCurrent = a.versionId === currentVersionId;
      const bIsCurrent = b.versionId === currentVersionId;

      if (aIsCurrent && !bIsCurrent) return -1;
      if (!aIsCurrent && bIsCurrent) return 1;

      const aTime =
        typeof a?.publishedAt?.toDate === "function"
          ? a.publishedAt.toDate().getTime()
          : 0;
      const bTime =
        typeof b?.publishedAt?.toDate === "function"
          ? b.publishedAt.toDate().getTime()
          : 0;

      return bTime - aTime;
    });
  }, [versionBatches, currentVersionId]);

  useEffect(() => {
    if (!hasAutoExpanded && currentVersionId) {
      setExpandedId(currentVersionId);
      setHasAutoExpanded(true);
    }
  }, [currentVersionId, hasAutoExpanded]);

  useEffect(() => {
    if (currentVersionRef.current) {
      currentVersionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [sortedBatches]);

  if (!sortedBatches.length) {
    return (
      <div className="charges-version-history">
        <h3 className="charges-version-history-title">Version History</h3>
        <div className="charges-version-empty">No published versions yet.</div>
      </div>
    );
  }

  return (
    <div className="charges-version-history">
      <div className="charges-version-history-header">
        <h3 className="charges-version-history-title">Version History</h3>
        {/* {currentVersionId && (
          <div className="charges-version-history-subtitle">
            Current version: {currentVersionId}
          </div>
        )} */}
      </div>

      <div className="charges-version-list">
        {sortedBatches.map((batch) => {
          // const isExpanded = false;
          const isExpanded = expandedId === batch.versionId;
          const isCurrent = batch.versionId === currentVersionId;

          const catalogEntries = Object.values(batch.catalogs || {}).sort(
            (a, b) =>
              (catalogOrder[a.catalogId] || 999) -
              (catalogOrder[b.catalogId] || 999),
          );

          return (
            <div
              key={batch.versionId}
              ref={isCurrent ? currentVersionRef : null}
              className={`charges-version-card ${
                isCurrent ? "current-version" : ""
              }`}
            >
              <div className="charges-version-top">
                <div className="charges-version-top-left">
                  <div className="charges-version-title-row">
                    <div className="charges-version-title">
                      {batch.label || batch.versionId}
                    </div>

                    {isCurrent && (
                      <span className="charges-version-badge">
                        Current Version
                      </span>
                    )}
                  </div>

                  <div className="charges-version-meta">
                    <span>
                      <strong>Published:</strong>{" "}
                      {formatDateTime(batch.publishedAt)}
                    </span>
                    <span>
                      <strong>By:</strong>{" "}
                      {batch.publishedBy?.name ||
                        batch.publishedBy?.email ||
                        "-"}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  className={`charges-version-toggle-btn ${
                    isCurrent ? "current-version-btn" : ""
                  }`}
                  onClick={() =>
                    setExpandedId((prev) =>
                      prev === batch.versionId ? null : batch.versionId,
                    )
                  }
                >
                  {isExpanded ? "Hide details" : "View details"}
                </button>
              </div>

              {isExpanded && (
                <div className="charges-version-details">
                  {catalogEntries.length ? (
                    catalogEntries.map((catalog) => (
                      <div
                        key={catalog.catalogId}
                        className="charges-version-catalog-section"
                      >
                        <div className="charges-version-catalog-header">
                          <h4 className="Content-SemiBold primary">
                            {catalog.catalogName}
                          </h4>

                          {catalog.notes && (
                            <div className="charges-version-catalog-notes">
                              <strong>Notes:</strong> {catalog.notes || "-"}
                            </div>
                          )}
                        </div>

                        {(catalog.snapshot?.categories || []).map(
                          (category) => (
                            <div
                              key={category.id}
                              className="charges-version-category"
                            >
                              <h5 className="charges-version-category-title">
                                {category.name}
                              </h5>

                              {(category.items || []).length ? (
                                (category.items || []).map((item) => (
                                  <div
                                    key={item.id}
                                    className="charges-version-item"
                                  >
                                    <div className="charges-version-item-left">
                                      <div className="charges-version-item-name">
                                        {item.name}
                                      </div>

                                      {!!item.description && (
                                        <div className="charges-version-item-description">
                                          {item.description}
                                        </div>
                                      )}

                                      {!!item.modifiers?.length && (
                                        <div className="charges-version-modifiers">
                                          {item.modifiers.map((modifier) => (
                                            <div
                                              key={modifier.id}
                                              className="charges-version-modifier"
                                            >
                                              +{" "}
                                              {formatCharge(
                                                modifier.amount,
                                                "GBP",
                                                "",
                                              )}{" "}
                                              per {modifier.label}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>

                                    <div className="charges-version-item-right">
                                      {formatCharge(
                                        item.amount,
                                        item.currency,
                                        item.amountPrefix,
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="charges-version-no-items">
                                  No charges in this category.
                                </div>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="charges-version-empty">
                      No catalog data found for this version.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChargesVersionHistory;
